import { getSeedingCampaignDetail } from '@musinsa/apis-curator';
import { withServiceErrorCapture } from '@musinsa/services-common-service-zod4';

import type { GetSeedingProposalDetailServiceResponse } from './get-seeding-proposal-detail.dto';
import type { GetSeedingRecruitmentDetailServiceResponse } from './get-seeding-recruitment-detail.dto';
import {
  determineSeedingProposalScreenState,
  determineSeedingRecruitmentScreenState,
} from './utils';

/**
 * 통일 시딩 상세 — campaignId 만으로 모집/제안 상세를 도출한다 (참여-후 단일 라우트용).
 *
 * api 가 participationType 으로 kind 를 판별해 응답하면, kind 별 screenState 도출 + DTO 구성.
 * 화면(features adapter)은 `kind` 로 분기해 `SeedingCampaignDetail` 로 매핑한다.
 */
export type SeedingCampaignDetailServiceResponse =
  | ({ kind: 'recruit' } & GetSeedingRecruitmentDetailServiceResponse)
  | ({ kind: 'propose' } & GetSeedingProposalDetailServiceResponse);

export const getSeedingCampaignDetailService = withServiceErrorCapture(
  async (campaignId: number): Promise<SeedingCampaignDetailServiceResponse> => {
    const result = await getSeedingCampaignDetail(campaignId);

    if (result.response.meta?.result === 'FAIL') {
      throw new Error(result.response.meta?.message ?? '시딩 캠페인 상세 조회에 실패했습니다.');
    }

    if (result.kind === 'recruit') {
      const data = result.response.data;
      return {
        kind: 'recruit',
        screenState: determineSeedingRecruitmentScreenState(data, Date.now()),
        campaign: {
          ...data.campaign,
          // requiredHashtags 는 각 항목에 # 가 이미 포함됨 → 공백 join 만으로 표시·복사용 문자열 구성.
          hashtagText: data.campaign.requiredHashtags.join(' '),
        },
        goods: data.goods,
        participation: data.participation,
        fulfillments: data.fulfillments,
        contentSubmissions: data.contentSubmissions,
        deliveryAddress: data.deliveryAddress,
        penalty: data.penalty,
        shippingDeadlineDays: data.shippingDeadlineDays ?? null,
        estimatedShippingDeadlineAt: data.estimatedShippingDeadlineAt ?? null,
      };
    }

    const data = result.response.data;
    return {
      kind: 'propose',
      screenState: determineSeedingProposalScreenState(data),
      campaign: {
        ...data.campaign,
        // requiredHashtags 는 각 항목에 # 가 이미 포함됨 → 공백 join 만으로 표시·복사용 문자열 구성.
        hashtagText: data.campaign.requiredHashtags.join(' '),
      },
      goods: data.goods,
      participation: data.participation,
      fulfillments: data.fulfillments,
      contentSubmissions: data.contentSubmissions,
      deliveryAddress: data.deliveryAddress,
      penalty: data.penalty,
      shippingDeadlineDays: data.shippingDeadlineDays ?? null,
      estimatedShippingDeadlineAt: data.estimatedShippingDeadlineAt ?? null,
    };
  },
);
